/** The spend-based multiplier sets, read - **gate 20, ADR-0044**.
 *
 * `GET /api/v1/spend-factor-sets` returns each set's *provenance* and a count, never a multiplier:
 * who published it, which edition, when it was retrieved, what its prices are denominated in, and
 * the publisher's own caveat about the use this product puts it to. It lives beside
 * `api/factors.ts` rather than in it because that module's export list is pinned by a test as
 * "a read and nothing that changes or chooses", and a second read is a second module, not a wider
 * one.
 *
 * Read-only for the reason the per-activity library is: a set is locked when it is loaded
 * (ADR-0012) and a publisher's correction is a new edition. There is no mutation here, no edition
 * argument and no custom multiplier.
 *
 * A response in the wrong shape is a failed read, never a library with fewer sets in it; and
 * `{ sets: [] }` is a real answer (a deployment the loader has not run against) and is returned as
 * itself. The two can never be rendered alike.
 */

import { useQuery } from "@tanstack/react-query";

import { apiFetch } from "./client";
import { refusalFrom } from "./refusal";
import type { components } from "./schema";

export type SpendFactorSet = components["schemas"]["FactorLibrarySpendSetOut"];
export type SpendFactorSets = components["schemas"]["FactorLibrarySpendSetsOut"];

const STRINGS = [
  "key",
  "label",
  "jurisdiction",
  "publisher",
  "edition",
  "retrieved_at",
  "currency",
  "price_basis",
  "classification",
  "vat_treatment",
  "gwp_statement",
  "gwp_verification_row",
  "caveat",
  "verification_row",
] as const;
const NULLABLE_STRINGS = [
  "published_at",
  "source_url",
  "source_sha256",
  "licence",
  "attribution_statement",
  "locked_at",
] as const;
const NULLABLE_INTEGERS = [
  "price_basis_year",
  "gwp_horizon_years",
  "first_multiplier_year",
  "last_multiplier_year",
] as const;

const NOT_THE_SHAPE =
  "The spend-based multiplier sets came back in a shape this screen does not recognise, so nothing is shown rather than something wrong.";

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isSpendSet(value: unknown): value is SpendFactorSet {
  if (!isRecord(value)) return false;
  return (
    STRINGS.every((key) => typeof value[key] === "string") &&
    NULLABLE_STRINGS.every((key) => value[key] === null || typeof value[key] === "string") &&
    NULLABLE_INTEGERS.every((key) => value[key] === null || Number.isInteger(value[key])) &&
    typeof value.locked === "boolean" &&
    Number.isInteger(value.factor_count)
  );
}

/** The body, if it is what this screen was written for; otherwise an error. */
export function parseSpendFactorSets(body: unknown): SpendFactorSets {
  if (!isRecord(body) || !Array.isArray(body.sets) || !body.sets.every(isSpendSet)) {
    throw new Error(NOT_THE_SHAPE);
  }
  return { sets: body.sets };
}

export const spendFactorKeys = {
  sets: () => ["spend-factor-sets"] as const,
};

export function useSpendFactorSets() {
  return useQuery({
    queryKey: spendFactorKeys.sets(),
    retry: false,
    staleTime: 0,
    gcTime: 0,
    queryFn: async (): Promise<SpendFactorSets> => {
      const response = await apiFetch("/api/v1/spend-factor-sets");
      if (!response.ok) {
        const body: unknown = await response.json().catch(() => null);
        throw refusalFrom(response, body, "The spend-based multiplier sets could not be read.");
      }
      const body: unknown = await response.json().catch(() => null);
      return parseSpendFactorSets(body);
    },
  });
}
